/**
 * WorkspaceSelector - 워크스페이스 선택 드롭다운
 *
 * 헤더에 표시되는 워크스페이스 선택 UI입니다.
 * 현재 워크스페이스를 표시하고, 다른 워크스페이스로 전환할 수 있습니다.
 */

import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkspaceStore } from '../workspaceStore'
import { clearClientCache } from '../WorkspaceContext'
import { WorkspaceAddModal } from './WorkspaceAddModal'

/**
 * WorkspaceSelector 컴포넌트
 */
export function WorkspaceSelector() {
  const navigate = useNavigate()
  const { workspaces, activeWorkspaceId, setActiveWorkspace } = useWorkspaceStore()

  const [isOpen, setIsOpen] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 현재 활성 워크스페이스
  const activeWorkspace = workspaces.find((ws) => ws.id === activeWorkspaceId)

  /**
   * 드롭다운 외부 클릭 감지
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  /**
   * 워크스페이스 전환 처리
   */
  const handleSwitchWorkspace = (workspaceId: string) => {
    if (workspaceId === activeWorkspaceId) {
      setIsOpen(false)
      return
    }

    // 캐시 초기화
    clearClientCache()

    // 워크스페이스 전환
    setActiveWorkspace(workspaceId)

    // 페이지 새로고침
    window.location.reload()
  }

  /**
   * 가계부 관리 페이지로 이동
   */
  const handleManage = () => {
    setIsOpen(false)
    navigate('/settings/workspaces')
  }

  /**
   * 워크스페이스 추가 모달 열기
   */
  const handleOpenAddModal = () => {
    setIsOpen(false)
    setShowAddModal(true)
  }

  // 워크스페이스가 없으면 표시하지 않음 (온보딩 화면이 대신 표시됨)
  if (workspaces.length === 0) {
    return null
  }

  return (
    <>
      <div ref={dropdownRef} className="relative">
        {/* 드롭다운 버튼 */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium
                   text-gray-700 dark:text-gray-200
                   hover:bg-gray-100 dark:hover:bg-gray-700
                   rounded-md transition-colors"
        >
          <span className="max-w-[120px] truncate">
            {activeWorkspace?.name || '워크스페이스 선택'}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* 드롭다운 메뉴 */}
        {isOpen && (
          <div
            className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800
                     border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg
                     py-2 z-50"
          >
            {/* 워크스페이스 목록 */}
            <div className="max-h-64 overflow-y-auto">
              {workspaces.map((workspace) => (
                <button
                  key={workspace.id}
                  onClick={() => handleSwitchWorkspace(workspace.id)}
                  className={`w-full flex items-center space-x-2 px-4 py-2 text-left
                           transition-colors
                           ${
                             workspace.id === activeWorkspaceId
                               ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                               : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                           }`}
                >
                  {/* 활성 표시 */}
                  <span className="text-lg">
                    {workspace.id === activeWorkspaceId ? '●' : '○'}
                  </span>

                  {/* 워크스페이스 이름 */}
                  <span className="flex-1 truncate">{workspace.name}</span>
                </button>
              ))}
            </div>

            {/* 구분선 */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

            {/* 액션 버튼들 */}
            <button
              onClick={handleOpenAddModal}
              className="w-full flex items-center space-x-2 px-4 py-2 text-left
                       text-gray-700 dark:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700
                       transition-colors"
            >
              <span>+</span>
              <span>새 가계부 추가</span>
            </button>

            <button
              onClick={handleManage}
              className="w-full flex items-center space-x-2 px-4 py-2 text-left
                       text-gray-700 dark:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700
                       transition-colors"
            >
              <span>⚙️</span>
              <span>가계부 관리</span>
            </button>
          </div>
        )}
      </div>

      {/* 워크스페이스 추가 모달 */}
      {showAddModal && <WorkspaceAddModal onClose={() => setShowAddModal(false)} />}
    </>
  )
}
